'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'

const phases = [
  { id: 'blueprint', label: 'Blueprint', price: '$999' },
  { id: 'execution', label: 'Build / Academy', price: 'From $5,000' },
  { id: 'full-stack', label: 'Full Stack', price: 'From $10K' },
  { id: 'advisory', label: 'Advisory', price: 'Scoped' },
  { id: 'venture-studio', label: 'Venture Studio', price: '' },
]

export function PhaseNav() {
  const [activePhase, setActivePhase] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const sectionIds = ['blueprint', 'execution', 'full-stack', 'advisory', 'venture-studio']
    const heroEl = document.querySelector('section')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActivePhase(entry.target.id)
          }
        })
      },
      { rootMargin: '-40% 0px -55% 0px' }
    )

    const heroObserver = new IntersectionObserver(
      ([entry]) => setIsVisible(!entry.isIntersecting),
      { threshold: 0.1 }
    )

    sectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    if (heroEl) heroObserver.observe(heroEl)

    return () => {
      observer.disconnect()
      heroObserver.disconnect()
    }
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.nav
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-[60] backdrop-blur-xl bg-[#1d212a]/80 border-b border-[#3e4658]/50"
        >
          <div className="container max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between h-14 overflow-x-auto scrollbar-hide">
              <div className="flex items-center gap-1 md:gap-3 min-w-max font-anuphan">
                {phases.map((phase, i) => (
                  <div key={phase.id} className="flex items-center">
                    <button
                      onClick={() => scrollTo(phase.id)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap',
                        activePhase === phase.id
                          ? 'bg-[#61d8fe]/10 text-[#61d8fe]'
                          : 'text-[#8a8f9e] hover:text-white'
                      )}
                    >
                      <span className="font-semibold">{phase.label}</span>
                      <span className="text-xs opacity-60 hidden sm:inline">{phase.price}</span>
                    </button>
                    {i < phases.length - 1 && (
                      <div className={cn(
                        'w-4 md:w-8 h-px mx-1',
                        activePhase && phases.findIndex(p => p.id === activePhase) > i
                          ? 'bg-[#61d8fe]/40'
                          : 'bg-[#3e4658]/50'
                      )} />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => scrollTo('blueprint')}
                className="ml-4 px-4 py-1.5 rounded-full bg-[#544ae8] text-white text-xs font-semibold font-anuphan whitespace-nowrap hover:bg-[#544ae8]/80 transition-colors hidden md:block"
              >
                Get Started
              </button>
            </div>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  )
}
